import React, { useEffect, useRef, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';

// React-Select style object adapted to this custom component
const defaultStyles = {
  control: (provided, state) => ({
    ...provided,
    border: '1px solid #E6E6E6',
    borderRadius: '8px',
    minHeight: '48px',
    paddingRight: '5px',
    paddingLeft: '5px',
    background: '#F5F5F5',
    boxShadow: state.isFocused ? '0 0 0 2px #5D4A9D' : 'none',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9CA3AF',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#111827',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '8px',
    padding: '8px 12px',
    border: '1px solid #E0DEF7',
    boxShadow: '0px 10px 10px 0px #100A551A',
    background: '#ffffff',
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius:"8px",
    backgroundColor: state.isSelected ? '#5D4A9D1A' : state.isFocused ? '#5D4A9D1A' : 'white',
    color: state.isSelected ? '#5D4A9D' : '#111827',
  }),
};

export default function CustomSelect({ options = [], value, onChange, onBlur, placeholder = 'Select', disabled = false, styles }) {
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(-1);
  const containerRef = useRef(null);

  const s = { ...defaultStyles, ...(styles || {}) };

  const selected = (() => {
    if (!value) return null;
    if (typeof value === 'object' && value.value !== undefined) return value;
    return options.find((o) => String(o.value) === String(value)) || null;
  })();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    onChange?.(opt);
    setOpen(false);
  };

  const controlBase = {
    width: '100%',
    marginTop:'10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textAlign: 'left',
    fontSize: '14px',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
  const controlStyle = s.control(controlBase, { isFocused, isDisabled: disabled });
  const placeholderStyle = s.placeholder({}, {});
  const singleValueStyle = s.singleValue({}, {});
  const menuStyle = s.menu({ position: 'absolute', zIndex: 20, marginTop: '4px', width: '100%', maxHeight: '14rem', overflow: 'auto' }, {});

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onFocus={() => setIsFocused(true)}
        onBlur={() => { setIsFocused(false); onBlur?.(); }}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={controlStyle}
        className={`!px-4 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <span style={selected ? singleValueStyle : placeholderStyle} className="truncate">
          {selected ? selected.label : placeholder}
        </span>
        <FaChevronDown className="text-gray-500" size={12} />
      </button>

      {open && (
        <ul style={menuStyle} className="absolute">
          {options.map((opt, idx) => {
            const isSelected = selected?.value === opt.value;
            const optionStyle = s.option({ padding: '8px 12px', fontSize: '14px', cursor: 'pointer' }, { isSelected, isFocused: hoverIndex === idx });
            return (
              <li
                key={opt.value}
                style={optionStyle}
                onMouseEnter={() => setHoverIndex(idx)}
                onMouseLeave={() => setHoverIndex(-1)}
                onClick={() => handleSelect(opt)}
              >
                {opt.label}
              </li>
            );
          })}
          {options.length === 0 && (
            <li style={s.option({ padding: '8px 12px', fontSize: '14px' }, { isSelected: false, isFocused: false })} className="text-[#7D7D7D]">No options</li>
          )}
        </ul>
      )}
    </div>
  );
}